import { Reply } from "@/types";
import { useEffect, useState } from "react";
import ReplyEditor from "./ReplyEditor";
import AppText from "@/components/AppText";
import AppBox from "@/components/AppBox";
import { RepliesGroup } from "./RepliesGroup";
import AfterHoursButton from "../AfterHoursButton";
import { useAppSelector } from "@/store/hooks";

interface Props {
  replies: Reply[];
  onChange: (updated: Reply[]) => void;
}

const MissedCallReplies: React.FC<Props> = ({ replies, onChange }) => {
  const reply = replies.find(r => r.type === "call" && !r.is_after_hours);
  const afterHoursReply = replies.filter(r => r.type === "call" && r.is_after_hours);

  const [form, setForm] = useState<Record<number, Partial<Reply>>>({});
  const { me } = useAppSelector((state) => state.auth);
  const afterHoursEnabled = me?.after_hours_enabled ?? false;

  // Initialize form state for every reply
  useEffect(() => {
    const initial: Record<number, Partial<Reply>> = {};
    replies.forEach(r => {
      initial[r.id] = {
        text: r.text,
        delay: r.delay,
        active: r.active,
      };
    });
    setForm(initial);
  }, [replies]);

  const handleChange = (id: number, field: keyof Reply, value: any) => {
    const updatedReplies = replies.map(r =>
      r.id === id ? { ...r, [field]: value } : r
    );
    setForm(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
    onChange(updatedReplies);
  };

  const handleToggleActive = (id: number) => {
    const updatedReplies = replies.map(r =>
      r.id === id ? { ...r, active: !r.active } : r
    );
    setForm(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        active: !prev[id]?.active,
      },
    }));
    onChange(updatedReplies);
  };

  return reply ? (
    <AppBox column gap={12}>
      <ReplyEditor
        reply={reply}
        form={form[reply.id] || {}}
        onChange={handleChange}
        onToggleActive={() => handleToggleActive(reply.id)}
      />

      <AppBox className="border-t border-gray-400" />
      <RepliesGroup
        title="After Hours Message"
        replies={afterHoursReply}
        form={form}
        onChange={handleChange}
        onToggleActive={handleToggleActive}
        disabled={!afterHoursEnabled}
        buttonSlot={afterHoursEnabled ? <AfterHoursButton /> : undefined}
        isAfterHours={true}
      />
      
    </AppBox>
  ) : (
    <AppBox className="px-4 py-6">
      <AppText>No missed call replies configured.</AppText>
    </AppBox>
  );
};

export default MissedCallReplies;